/**
 * ClarificationPanel — inline pre-flight clarification gate UI.
 *
 * Rendered on /investigate when the backend returns HTTP 202 with 1-3
 * clarifying questions. Each question is presented as 4 buttons: 3
 * model-generated options + a fourth "Other" button that reveals a
 * free-form text input. The user picks an option per question, then
 * submits → the FE re-POSTs with the answers appended to the original
 * prompt. The exchange may iterate; the panel re-renders with fresh
 * questions when the backend returns another 202.
 *
 * UX (single question shown for brevity):
 *
 *   > round 1 of 3 · need 1 more detail before I dig in:
 *
 *      1. Which service or endpoint did this affect?
 *      [ detect-service ] [ fieldmap-workers ] [ gce-proxy ] [ Other ]
 *      [_____________________________________________]   ← only shown
 *                                                          when Other is picked
 *
 *      flags: broad_window_no_service
 *
 *      [ submit & investigate ]  [ skip — run anyway ]  [ ← edit prompt ]
 */

import { useCallback, useState } from 'react';

import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type {
  ClarificationQuestion,
  ClarificationRequiredResponse,
} from '@/types/investigation';

const MONO = '"JetBrains Mono", "Fira Code", "SF Mono", monospace';
const OTHER_SENTINEL = '__other__';

export interface ClarificationAnswer {
  /** Which option was clicked, or OTHER_SENTINEL when the user chose "Other". */
  selected: string | null;
  /** Free-form text typed when ``selected === OTHER_SENTINEL``. Ignored otherwise. */
  otherText: string;
}

/**
 * Collapse a per-question answer to the single string the FE should send
 * to the backend ("" when unanswered). Exported so the hook can use the
 * same mapping when building the augmented prompt.
 */
export function answerToString(a: ClarificationAnswer | undefined): string {
  if (!a || a.selected === null) return '';
  if (a.selected === OTHER_SENTINEL) return a.otherText.trim();
  return a.selected;
}

export interface ClarificationPanelProps {
  payload: ClarificationRequiredResponse;
  /** Iteration round, 1-based. Surfaced as "round X of N" in the header. */
  round: number;
  /** Maximum rounds before the user is offered "run anyway" with no more questions. */
  maxRounds: number;
  onSubmit: (
    answers: Record<number, ClarificationAnswer>,
    mode: 'augment' | 'skip',
  ) => void;
  onEdit: (answers: Record<number, ClarificationAnswer>) => void;
  submitting?: boolean;
}

export function ClarificationPanel({
  payload,
  round,
  maxRounds,
  onSubmit,
  onEdit,
  submitting = false,
}: ClarificationPanelProps): JSX.Element {
  const [answers, setAnswers] = useState<Record<number, ClarificationAnswer>>({});

  const setSelected = useCallback((idx: number, choice: string) => {
    setAnswers((prev) => {
      const existing = prev[idx];
      return {
        ...prev,
        [idx]: {
          selected: choice,
          otherText: existing?.otherText ?? '',
        },
      };
    });
  }, []);

  const setOtherText = useCallback((idx: number, text: string) => {
    setAnswers((prev) => {
      const existing = prev[idx];
      return {
        ...prev,
        [idx]: {
          selected: existing?.selected ?? OTHER_SENTINEL,
          otherText: text,
        },
      };
    });
  }, []);

  // Submit is enabled once every question has a non-empty answer (option
  // picked, or Other+text typed). Partial submits would re-fire the same
  // questions on the next round.
  const allAnswered = payload.questions.every((_, idx) => {
    return answerToString(answers[idx]).length > 0;
  });

  const headerSuffix = (() => {
    const remaining = Math.max(maxRounds - round, 0);
    const nQ = payload.questions.length;
    if (remaining === 0) {
      // Last allowed round — be transparent.
      return `final clarification round (${nQ} ${
        nQ === 1 ? 'question' : 'questions'
      }):`;
    }
    return `round ${round} of ${maxRounds} · ${nQ} more ${
      nQ === 1 ? 'detail' : 'details'
    } before I dig in:`;
  })();

  return (
    <Box
      role="region"
      aria-label="Clarification questions"
      sx={{
        fontFamily: MONO,
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        component="div"
        sx={{
          fontFamily: MONO,
          fontSize: '0.95rem',
          color: 'text.primary',
          mb: 2,
        }}
      >
        <Box component="span" sx={{ color: 'success.main', mr: 1 }}>
          &gt;
        </Box>
        {headerSuffix}
      </Typography>

      <Stack spacing={2.5} sx={{ ml: 2.5, mb: 2 }}>
        {payload.questions.map((question, idx) => (
          <QuestionBlock
            key={`${round}-${idx}`}
            idx={idx}
            question={question}
            answer={answers[idx]}
            onSelect={(choice) => setSelected(idx, choice)}
            onOtherChange={(text) => setOtherText(idx, text)}
            disabled={submitting}
          />
        ))}
      </Stack>

      {payload.ambiguity_flags.length > 0 && (
        <Typography
          component="div"
          sx={{
            fontFamily: MONO,
            fontSize: '0.75rem',
            color: 'text.secondary',
            ml: 2.5,
            mb: 2,
          }}
          aria-label="Ambiguity flags"
        >
          flags: {payload.ambiguity_flags.join(', ')}
        </Typography>
      )}

      <Stack direction="row" spacing={1.5} sx={{ ml: 2.5 }}>
        <Button
          variant="contained"
          onClick={() => onSubmit(answers, 'augment')}
          disabled={submitting || !allAnswered}
          sx={{ fontFamily: MONO, textTransform: 'none' }}
        >
          submit &amp; investigate
        </Button>
        <Button
          variant="outlined"
          onClick={() => onSubmit(answers, 'skip')}
          disabled={submitting}
          sx={{ fontFamily: MONO, textTransform: 'none' }}
        >
          skip — run anyway
        </Button>
        <Button
          variant="text"
          onClick={() => onEdit(answers)}
          disabled={submitting}
          sx={{ fontFamily: MONO, textTransform: 'none' }}
        >
          ← edit prompt
        </Button>
      </Stack>
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Subcomponent — one question block (label + 4 buttons + optional textbox)
// ---------------------------------------------------------------------------


interface QuestionBlockProps {
  idx: number;
  question: ClarificationQuestion;
  answer: ClarificationAnswer | undefined;
  onSelect: (choice: string) => void;
  onOtherChange: (text: string) => void;
  disabled: boolean;
}

function QuestionBlock({
  idx,
  question,
  answer,
  onSelect,
  onOtherChange,
  disabled,
}: QuestionBlockProps): JSX.Element {
  const selected = answer?.selected ?? null;
  const otherActive = selected === OTHER_SENTINEL;
  return (
    <Box>
      <Typography
        component="div"
        sx={{
          fontFamily: MONO,
          fontSize: '0.9rem',
          color: 'text.primary',
          mb: 1,
        }}
      >
        {idx + 1}. {question.text}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: 'wrap', rowGap: 1 }}
        useFlexGap
        role="radiogroup"
        aria-label={`Options for question ${idx + 1}`}
      >
        {question.options.map((opt) => {
          const active = selected === opt;
          return (
            <Button
              key={opt}
              size="small"
              variant={active ? 'contained' : 'outlined'}
              onClick={() => onSelect(opt)}
              disabled={disabled}
              role="radio"
              aria-checked={active}
              sx={{
                fontFamily: MONO,
                fontSize: '0.8rem',
                textTransform: 'none',
                minHeight: 36,
              }}
            >
              {opt}
            </Button>
          );
        })}
        <Button
          key="__other__"
          size="small"
          variant={otherActive ? 'contained' : 'outlined'}
          onClick={() => onSelect(OTHER_SENTINEL)}
          disabled={disabled}
          role="radio"
          aria-checked={otherActive}
          sx={{
            fontFamily: MONO,
            fontSize: '0.8rem',
            textTransform: 'none',
            minHeight: 36,
            fontStyle: 'italic',
          }}
        >
          Other…
        </Button>
      </Stack>
      {otherActive && (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="describe in one sentence…"
            value={answer?.otherText ?? ''}
            onChange={(e) => onOtherChange(e.target.value)}
            disabled={disabled}
            inputProps={{
              'aria-label': `Other answer for question ${idx + 1}`,
              style: { fontFamily: MONO, fontSize: '0.85rem' },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
